import { IMPORTAR_GRUPO_USUARIOS } from '../data/migracao-mutation'
import { useCallback, useState } from 'react'
import { SINCRONIZAR_DEALERS } from '@/modules/super-admin/data/sincronizacao-dealers'
import { SINCRONIZAR_GRUPOS } from '@/modules/super-admin/data/sincronizacao-grupos'
import { SINCRONIZAR_IDENTITY_PROVIDER } from '@/modules/super-admin/data/sincronizacao-identity-provider'
import { SINCRONIZAR_USUARIOS } from '@/modules/super-admin/data/sincronizacao-usuarios'
import {
  EditarProdutoMutationMutation,
  EditarProdutoMutationMutationVariables,
  ImportarGrupoUsuariosMutationMutation,
  ImportarGrupoUsuariosMutationMutationVariables,
  LinksExternosInput,
  LinksExternosQueryQuery,
  LinksExternosQueryQueryVariables,
  ProdutosQueryQuery,
  ProdutosQueryQueryVariables,
  SincronizarDealersMutationMutation,
  SincronizarDealersMutationMutationVariables,
  SincronizarGruposMutationMutation,
  SincronizarGruposMutationMutationVariables,
  SincronizarIdentityProviderMutation,
  SincronizarIdentityProviderMutationVariables,
  SincronizarUsuariosMutationMutation,
  SincronizarUsuariosMutationMutationVariables
} from '@/core/types/graphql'
import { useConfirmModal } from '@/core/context/confirm-modal-context'

import { MessagePlaceholder, t } from '@lingui/core/macro'
import {
  EDITAR_PRODUTO,
  PRODUTOS_QUERY
} from '@/modules/super-admin/data/produtos-query'
import { getLabel } from '@/core/utils/global-labels'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { request } from '../../../core/utils/request'
import { LINKS_EXTERNOS } from '../data/links-externos'

export const useSuperAdminQueryHandler = () => {
  const { askUser } = useConfirmModal()
  const [produtoId, setProdutoId] = useState<string>('')

  const { data } = useQuery({
    queryKey: ['produtos'],
    queryFn: async () =>
      await request<ProdutosQueryQuery, ProdutosQueryQueryVariables>(
        PRODUTOS_QUERY
      )
  })

  const client = useQueryClient()

  const { data: linksExternosData } = useQuery({
    queryKey: ['linksExternos', { produtoId }],
    queryFn: async () =>
      await request<LinksExternosQueryQuery, LinksExternosQueryQueryVariables>(
        LINKS_EXTERNOS,
        {
          variables: {
            id: produtoId
          }
        }
      )
  })

  const importarGrupoUsuarios = useMutation({
    mutationKey: ['importarGrupoUsuarios'],
    mutationFn: async ({
      variables
    }: {
      variables: ImportarGrupoUsuariosMutationMutationVariables
    }) =>
      await request<
        ImportarGrupoUsuariosMutationMutation,
        ImportarGrupoUsuariosMutationMutationVariables
      >(IMPORTAR_GRUPO_USUARIOS, { variables })
  })

  const sincronizarGrupos = useMutation({
    mutationKey: ['sincronizarGrupos'],
    mutationFn: async ({
      variables
    }: {
      variables: SincronizarGruposMutationMutationVariables
    }) =>
      await request<
        SincronizarGruposMutationMutation,
        SincronizarGruposMutationMutationVariables
      >(SINCRONIZAR_GRUPOS, { variables })
  })

  const sincronizarDealers = useMutation({
    mutationKey: ['sincronizarDealers'],
    mutationFn: async ({
      variables
    }: {
      variables: SincronizarDealersMutationMutationVariables
    }) =>
      await request<
        SincronizarDealersMutationMutation,
        SincronizarDealersMutationMutationVariables
      >(SINCRONIZAR_DEALERS, { variables })
  })

  const sincronizarUsuarios = useMutation({
    mutationKey: ['sincronizarUsuarios'],
    mutationFn: async ({
      variables
    }: {
      variables: SincronizarUsuariosMutationMutationVariables
    }) =>
      await request<
        SincronizarUsuariosMutationMutation,
        SincronizarUsuariosMutationMutationVariables
      >(SINCRONIZAR_USUARIOS, { variables })
  })

  const sincronizarIdentityProvider = useMutation({
    mutationKey: ['sincronizarIdentityProvider'],
    mutationFn: async ({
      variables
    }: {
      variables: SincronizarIdentityProviderMutationVariables
    }) =>
      await request<
        SincronizarIdentityProviderMutation,
        SincronizarIdentityProviderMutationVariables
      >(SINCRONIZAR_IDENTITY_PROVIDER, { variables })
  })

  const editarProduto = useMutation({
    mutationKey: ['editarProduto'],
    mutationFn: async ({
      variables
    }: {
      variables: EditarProdutoMutationMutationVariables
    }) =>
      await request<
        EditarProdutoMutationMutation,
        EditarProdutoMutationMutationVariables
      >(EDITAR_PRODUTO, { variables })
  })

  const handleEditarProduto = useCallback(
    (
      produtoId: string,
      baseUriHomolog: string,
      linksExternos: LinksExternosInput[]
    ) => {
      askUser({
        onConfirm: async () => {
          await editarProduto.mutateAsync({
            variables: {
              produto: {
                id: produtoId,
                baseUriHomolog,
                linksExternos
              }
            }
          })
          await client.refetchQueries({ queryKey: ['menu'] })
        },
        title: t`Você realmente deseja atualizar?`,
        confirmLabel: t`Atualizar`,
        successMessage: t`Atualizado com sucesso!`,
        errorMessage: t`Não foi possível atualizar`
      })
    },
    [editarProduto, askUser, client]
  )

  const handleImportarGrupoUsuarios = useCallback(() => {
    askUser({
      onConfirm: async () => {
        try {
          await importarGrupoUsuarios.mutateAsync({ variables: {} })
        } catch (error) {
          console.error('Erro ao migrar grupo de usuários:', error)
          throw error
        }
      },
      title: t`Quer migrar o grupo de usuários?`,
      confirmLabel: t`Migrar`,
      successMessage: t`Migrado com sucesso!`,
      errorMessage: t`Não foi possível migrar o grupo`
    })
  }, [importarGrupoUsuarios, askUser])

  const handleSincronizarGrupos = useCallback(() => {
    askUser({
      onConfirm: async () => {
        try {
          await sincronizarGrupos.mutateAsync({ variables: {} })
        } catch (error) {
          console.error(
            `Erro ao sincronizar ${getLabel('label.grupos')}:`,
            error
          )
          throw error
        }
      },
      title: t`Quer sincronizar ${getLabel('label.grupos') as MessagePlaceholder}?`,
      confirmLabel: t`Sincronizar`,
      successMessage: t`Sincronizado com sucesso!`,
      errorMessage: t`Não foi possível sincronizar ${getLabel('label.grupos') as MessagePlaceholder}`
    })
  }, [sincronizarGrupos, askUser])

  const handleSincronizarUsuarios = useCallback(() => {
    askUser({
      onConfirm: async () => {
        try {
          await sincronizarUsuarios.mutateAsync({ variables: {} })
        } catch (error) {
          console.error('Erro ao sincronizar usuários:', error)
          throw error
        }
      },
      title: t`Quer sincronizar usuários?`,
      confirmLabel: t`Sincronizar`,
      successMessage: t`Sincronizado com sucesso!`,
      errorMessage: t`Não foi possível sincronizar os usuários`
    })
  }, [sincronizarUsuarios, askUser])

  const handleSincronizarDealers = useCallback(() => {
    askUser({
      onConfirm: async () => {
        try {
          await sincronizarDealers.mutateAsync({ variables: {} })
        } catch (error) {
          console.error(
            `Erro ao sincronizar ${getLabel('label.dealers')}:`,
            error
          )
          throw error
        }
      },
      title: t`Quer sincronizar ${getLabel('label.dealers') as MessagePlaceholder}?`,
      confirmLabel: t`Sincronizar`,
      successMessage: t`Sincronizado com sucesso!`,
      errorMessage: t`Não foi possível sincronizar ${getLabel('label.dealers') as MessagePlaceholder}`
    })
  }, [sincronizarDealers, askUser])

  return {
    handleImportarGrupoUsuarios,
    handleSincronizarGrupos,
    handleSincronizarUsuarios,
    handleSincronizarDealers,
    sincronizarIdentityProvider,
    produtos: data?.produtos ?? [],
    handleEditarProduto,
    linksExternosData: linksExternosData ?? {},
    setProdutoId
  }
}
